// palbe-gen command logic: read the committed Palbase/ spec (or fetch it from
// the origin passed with --url), run the parser+emitter, and write palbe.gen.ts.
// Ports the CLI's Go semantics (internal/backend: pullTSTypes zero-op guard,
// --soft policy, tswatch.go watch loop).

import { createHash } from 'node:crypto';
import { mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname, isAbsolute, join, relative, resolve, sep } from 'node:path';
import process from 'node:process';
import { parseArgs } from 'node:util';
import { parseOAuthConfig } from '@palbase/auth';
import { environmentRefFromPublishableApiKey } from '../api-key.js';
import { emitEnvironmentConfigModule, emitTypeScript, type GeneratedConfig } from './emitter.js';
import { type Op, parseOpenAPI, parseRooms, type RoomDecl, readSpecDeploy } from './parser.js';

export type Writer = (line: string) => void;

const USAGE = `Usage: palbe-gen [--dir palbase/environments] [--out palbe.gen.ts] [--url <specOrigin>] [--soft] [--watch]

Generate the typed palbe.gen.ts web client for the @palbase/web SDK.

The environment is chosen by the PALBASE_ENV environment variable; unset means
\`local\`, the stack every checkout gets for free. Inputs are read from that
environment's own directory, and \`palbase/client.ts\` is written as the one line
your application imports — so switching environments never edits your source.

  --dir <dir>    Directory holding the per-environment artifacts written by
                 \`palbase link\`: <dir>/<env>/{openapi.json, web-config.json}
                 (default: palbase/environments). Role definitions travel
                 inside openapi.json as \`x-palbase-roles\`.
  --out <file>   Output file name, written inside the environment's directory;
                 an absolute path is used as given (default: palbe.gen.ts)
  --url <origin> Fetch the spec from <origin>/openapi.json instead of --dir
                 (e.g. a deployed Environment's origin)
  --soft         Never fail: print a warning and exit 0 on any error
                 (for predev/prebuild hooks)
  --watch        Poll the --url origin every second and regenerate whenever
                 the spec changes. Without --url, polls the linked
                 checkout's own Environment (the base_url committed to
                 <dir>/<env>/web-config.json by \`palbase link\`)
  -h, --help     Show this help`;

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export class HttpStatusError extends Error {
  readonly status: number;
  constructor(status: number, url: string) {
    super(`GET ${url}: HTTP ${status}`);
    this.status = status;
  }
}

export async function fetchSpec(url: string): Promise<string> {
  let res: Response;
  try {
    res = await fetch(url, { signal: AbortSignal.timeout(3000) });
  } catch (e) {
    throw new Error(`GET ${url}: ${errMsg(e)}`);
  }
  if (!res.ok) throw new HttpStatusError(res.status, url);
  return res.text();
}

// readWebConfig reads the required canonical app-bound
// {envDir}/web-config.json shape.
//
// The name carries the platform because one checkout can serve more than one
// (an RN app has web AND android beside each other in the same environment
// directory), and the environment directory is FLAT — the platform lives in the
// file name, not in a subdirectory. That flatness is load-bearing elsewhere:
// Xcode's exclusion pattern is written against a fixed number of path levels.
export function readWebConfig(dir: string): GeneratedConfig {
  const path = join(dir, 'web-config.json');
  let text: string;
  try {
    text = readFileSync(path, 'utf8');
  } catch {
    throw new Error(`${path} is required in dir mode — run \`palbase link\``);
  }
  let raw: unknown;
  try {
    raw = parseConfigJSON(text);
  } catch (e) {
    throw new Error(`${path} is not valid JSON: ${errMsg(e)}`);
  }
  const obj = (raw ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const required = (field: 'app_id' | 'base_url' | 'api_key'): string => {
    const value = str(obj[field]);
    if (value.trim() === '') {
      throw new Error(`${path} is missing nonempty required field ${field}`);
    }
    return value.trim();
  };
  const cfg: GeneratedConfig = {
    url: required('base_url'),
    apiKey: required('api_key'),
    appId: required('app_id'),
  };
  if (obj.sealed_root !== undefined) {
    const root = str(obj.sealed_root).trim();
    if (
      !/^[A-Za-z0-9+/]{43}=$/.test(root) ||
      Buffer.from(root, 'base64').toString('base64') !== root
    ) {
      throw new Error(`${path} sealed_root must be a base64 Ed25519 public key (32 bytes)`);
    }
    cfg.sealedRoot = root;
  }
  // The project identity comes from the key and from nowhere else. This file
  // used to require an `environment_ref` field beside it and refuse when the two
  // disagreed — and on 2026-08-16 they did: `palbase link` wrote one value while
  // minting a key carrying another, so linking a web app to a project produced a
  // config this generator would not read. A copy that must equal its original is
  // not a second fact, it is a second chance to be wrong.
  const projectRef = environmentRefFromPublishableApiKey(cfg.apiKey);
  if (projectRef === '') {
    throw new Error(`${path} api_key does not contain a valid publishable project identity`);
  }
  let configURL: URL;
  try {
    configURL = new URL(cfg.url);
  } catch {
    throw new Error(`${path} base_url must be a valid HTTPS URL`);
  }
  // A LOCAL STACK IS PLAIN HTTP ON LOOPBACK, and refusing it made two shipped
  // parts contradict each other: `palbase link http://127.0.0.1:PORT` succeeds and
  // docs/cli/local-stack says the local stack serves plain HTTP, while this threw
  // "base_url must be a valid HTTPS URL" — so `palbase link` could not generate
  // a client against the stack a person had just started (measured 2026-08-29).
  // Loopback carries no transport risk: the bytes never leave the machine.
  const isLoopback =
    configURL.hostname === '127.0.0.1' ||
    configURL.hostname === 'localhost' ||
    configURL.hostname === '[::1]' ||
    configURL.hostname === '::1';
  if (configURL.protocol !== 'https:' && !(configURL.protocol === 'http:' && isLoopback)) {
    throw new Error(
      `${path} base_url must be an HTTPS URL (plain http is accepted only for a loopback host such as 127.0.0.1 or localhost)`,
    );
  }
  // THE HOST IS NOT DERIVABLE FROM THE KEY, and a check that pretends otherwise
  // refuses every correct config.
  //
  // This used to require the first label of a *.palbase.studio host to equal the
  // ref inside the key, on the premise that a publishable key names its own
  // address. That premise was v1's. On v2 a stack's identity is a COMPILE-TIME
  // constant — every tenant's key reads `pb_project_…` (cloud
  // tenant-stack/main.go: bootStackRef, and palsvc panics at boot if a key
  // disagrees with it) — while the address is the tenant's own ref. So the two
  // never match, for any project, and the rule turned `palbe-gen` into something
  // that could not run against v2 at all.
  //
  // Nothing replaces it: there is no fact in the key to check the host against.
  // What DOES catch a config pointed at the wrong project is the generator's
  // next step — it fetches that host's contract with that key, and a mismatch
  // answers 401.
  if (Object.hasOwn(obj, 'auth') || Object.hasOwn(obj, 'socialAuth'))
    throw new Error(`${path} must use the oauth configuration emitted by palbase link`);
  if (Object.hasOwn(obj, 'oauth')) cfg.oauth = parseOAuthConfig(obj.oauth, projectRef);
  return cfg;
}

// JSON.parse keeps the last duplicate field. Validate the original document
// first so hand-edited config cannot hide a second client, target or revision.
function parseConfigJSON(raw: string): unknown {
  if (raw.length > 256 * 1024) throw new Error('App config exceeds 256 KiB');
  const value: unknown = JSON.parse(raw);
  let i = 0;
  const space = () => {
    while (/\s/.test(raw[i] ?? '') && i < raw.length) i++;
  };
  const string = () => {
    const start = i++;
    while (i < raw.length) {
      if (raw[i] === '\\') i += 2;
      else if (raw[i++] === '"') break;
    }
    return JSON.parse(raw.slice(start, i)) as string;
  };
  const visit = (depth: number) => {
    if (depth > 32) throw new Error('App config nesting exceeds 32 levels');
    space();
    if (raw[i] === '"') {
      string();
      return;
    }
    if (raw[i] === '{') {
      i++;
      const fields = new Set<string>();
      space();
      while (raw[i] !== '}') {
        const name = string();
        if (fields.has(name)) throw new Error(`Duplicate config field: ${name}`);
        fields.add(name);
        space();
        i++; // colon; syntax already checked by JSON.parse
        visit(depth + 1);
        space();
        if (raw[i] !== ',') break;
        i++;
        space();
      }
      i++;
      return;
    }
    if (raw[i] === '[') {
      i++;
      space();
      while (raw[i] !== ']') {
        visit(depth + 1);
        space();
        if (raw[i] !== ',') break;
        i++;
        space();
      }
      i++;
      return;
    }
    while (i < raw.length && !/[\s,}\]]/.test(raw[i] ?? '')) i++;
  };
  visit(0);
  return value;
}

function emitAndWrite(
  ops: Op[],
  cfg: GeneratedConfig,
  outFile: string,
  rooms: RoomDecl[],
  rolesArtifact: string,
): void {
  const dir = dirname(outFile);
  if (dir !== '.' && dir !== '') {
    mkdirSync(dir, { recursive: true });
  }
  writeFileSync(outFile, emitTypeScript(ops, cfg, rooms, rolesArtifact));
}

// readRolesFromSpec takes the role definitions out of the CONTRACT, where the
// stack now puts them (`x-palbase-roles`). There is no sibling file to find any
// more, so there is no path rule to get wrong.
//
// ABSENT IS NO LONGER AN ANSWER. It used to be: a missing sibling file returned
// '' and the module generated without role types, silently. That was tolerable
// only while the file was optional. Now a contract fetched by a CLI carrying
// this change ALWAYS has the field, so one without it is STALE — and emitting
// nothing for it would drop constants somebody's code already names, which is a
// build that keeps working while the permission behind it is gone.
//
// The two faults get two sentences, because they have two different cures.
export function readRolesFromSpec(specJson: string): string {
  const doc = JSON.parse(specJson) as Record<string, unknown>;
  const field = doc['x-palbase-roles'];
  if (field === undefined) {
    throw new Error(
      'this contract carries no `x-palbase-roles`, so it predates roles travelling inside ' +
        'the document. Two steps, in this order: upgrade `@palbase/backend` and push the ' +
        'project, then run `palbase link` to refresh the contract.',
    );
  }
  // PRESENT BUT BODILESS is the other fault: the producer answered about roles
  // without listing any. `{"roles": []}` is NOT this — that is a stack with no
  // roles, which is a known answer and generates empty constants.
  const body = field as { roles?: unknown };
  if (body === null || typeof body !== 'object' || !Array.isArray(body.roles)) {
    throw new Error(
      "this contract's `x-palbase-roles` carries no roles list, so what the stack defines " +
        'cannot be read from it. Refresh the contract; if it comes back the same, the stack ' +
        'is serving a malformed document.',
    );
  }
  return JSON.stringify(field);
}

function existingNonEmpty(outFile: string): boolean {
  try {
    return readFileSync(outFile).length > 0;
  } catch {
    return false;
  }
}

// writeGenerated applies the zero-op overwrite guard, then emits. A live spec
// with 0 operations almost always means the backend's controller metadata
// extraction broke (the "zero endpoints collected" failure class), not that
// the app has no endpoints. Never clobber a previously good palbe.gen.ts with
// an empty client; warn and exit 0 so a predev hook doesn't fail the build.
// With nothing to protect, write the (empty) module anyway — a fresh project
// still gets a compilable file — but warn loudly.
export function writeGenerated(
  ops: Op[],
  cfg: GeneratedConfig,
  outFile: string,
  out: Writer,
  specDeploy = '',
  rooms: RoomDecl[] = [],
  rolesArtifact = '',
): void {
  // THE ENVIRONMENT IS WRITTEN BEFORE THE ZERO-OP GUARD, because a config is an
  // IDENTITY, not an inventory: an environment with no endpoints is still that
  // environment. Below this point a zero-op run RETURNS to preserve an existing
  // palbe.gen.ts, and a config written after it would never happen.
  writeEnvironmentConfig(outFile, cfg);
  if (ops.length === 0) {
    if (existingNonEmpty(outFile)) {
      out(
        `warning: live spec has 0 operations — keeping existing ${outFile} (fix your controllers and rerun)`,
      );
      return;
    }
    out(
      `warning: live spec has 0 operations — ${outFile} registers no calls (fix your controllers and rerun)`,
    );
  }
  emitAndWrite(ops, cfg, outFile, rooms, rolesArtifact);
  out(`✓ wrote ${outFile} (${ops.length} operations${provenance(specDeploy)})`);
}

// provenance renders the deploy clause the success lines carry. Empty for a spec
// with no identity — an artifact built before the stamp existed. Saying nothing
// there is deliberate: a fabricated or guessed identity would read as verified.
function provenance(specDeploy: string): string {
  return specDeploy === '' ? '' : `, deploy ${specDeploy}`;
}

// resolveEnvironment picks the environment this run generates for.
//
// ONE KEY, AND ITS DEFAULT IS `local`. The customer writes `PALBASE_ENV` in
// whatever they already use (an npm script, a `.env`, their CI) and nothing else
// is asked of them — the CLI writes nothing into their build system.
//
// A blank value counts as UNSET, and that is not a nicety: a shell that exports
// `PALBASE_ENV=` sends an empty string, and reading that as a real name would
// look for an environment directory called "" and fail with a path nobody typed.
export function resolveEnvironment(value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? 'local' : trimmed;
}

// environmentDir is where ONE environment's committed artifacts live.
export function environmentDir(dir: string, environment: string): string {
  return join(dir, environment);
}

// barrelRootFor is the checkout root implied by --dir.
//
// `--dir` names the environments directory (`<root>/palbase/environments`), so
// the root is two levels up. Deriving it here rather than reading the process's
// working directory is what keeps the barrel beside the environments it points
// at — one visible `palbase/` per checkout, which is the whole rule.
export function barrelRootFor(dir: string): string {
  return resolve(dir, '..', '..');
}

// writeClientBarrel writes the ONE line the application imports.
//
// Every environment's generated client is committed, side by side. If the app
// imported one of them directly, its import path would carry the environment
// name — and switching environments would mean editing the app's own source.
// The barrel makes the import stable: the only thing that changes is this file's
// single re-export line.
//
// Measured (research.md): a bundler follows the re-export and pulls in ONLY the
// named environment's code — the unselected one leaves no trace in the bundle.
export function writeClientBarrel(root: string, outFile: string): void {
  const from = join(root, 'palbase');
  // POSIX separators: `relative` yields `\` on win32 and a backslash in an
  // ESM specifier is not a path separator, it is an escape.
  const rel = relative(from, resolve(outFile)).split(sep).join('/').replace(/\.ts$/, '');
  const target = rel.startsWith('.') ? rel : `./${rel}`;
  const body =
    `// GENERATED by \`palbase link\` / \`palbe-gen\` — do not edit.\n` +
    `// The environment is chosen by PALBASE_ENV (default: local).\n` +
    `export * from '${target}';\n`;
  mkdirSync(join(root, 'palbase'), { recursive: true });
  writeFileSync(join(root, 'palbase', 'client.ts'), body);
}

// writeEnvironmentConfig writes the leaf config module NEXT TO the generated
// client — derived from outFile, never from the environment directory.
//
// WHY NOT <envDir>: `--out` is honoured as given when it is absolute (main()),
// so the client can land outside the environment directory entirely. A config
// written to <envDir> would then not be the client's sibling, and the
// `./palbe.config` specifier palbe.gen.ts re-exports would resolve to nothing.
//
// IT PRINTS NOTHING. The watch tests assert the emitted line list with
// toEqual([...]), so an extra out() call here would break them — and there is
// nothing to announce: this file is a detail of the client beside it.
export function writeEnvironmentConfig(outFile: string, cfg: GeneratedConfig): string {
  const target = join(dirname(resolve(outFile)), 'palbe.config.ts');
  mkdirSync(dirname(target), { recursive: true });
  writeFileSync(target, emitEnvironmentConfigModule(cfg));
  return target;
}

// writeConfigBarrel is writeClientBarrel's twin for the config module.
//
// It derives its target from outFile rather than from writeEnvironmentConfig's
// return value, so the caller need not order the two: main() writes both
// barrels before it even knows whether this run is one-shot or a watch.
export function writeConfigBarrel(root: string, outFile: string): void {
  const configFile = join(dirname(resolve(outFile)), 'palbe.config.ts');
  const from = join(root, 'palbase');
  const rel = relative(from, configFile).split(sep).join('/').replace(/\.ts$/, '');
  const target = rel.startsWith('.') ? rel : `./${rel}`;
  const body =
    `// GENERATED by \`palbase link\` / \`palbe-gen\` — do not edit.\n` +
    `// The environment is chosen by PALBASE_ENV (default: local).\n` +
    `export * from '${target}';\n`;
  mkdirSync(from, { recursive: true });
  writeFileSync(join(from, 'config.ts'), body);
}

// generateOnce is the single-shot generation path.
//   - default: read {envDir}/openapi.json + required {envDir}/web-config.json
//   - --url:   fetch <origin>/openapi.json while retaining the canonical linked
//              Environment identity; only the runtime URL points at the origin.
export async function generateOnce(
  dir: string,
  outFile: string,
  url: string | undefined,
  out: Writer,
): Promise<void> {
  let specText: string;
  let cfg: GeneratedConfig;
  if (url !== undefined) {
    const origin = url.replace(/\/+$/, '');
    specText = await fetchSpec(`${origin}/openapi.json`);
    cfg = { ...readWebConfig(dir), url: origin };
  } else {
    cfg = readWebConfig(dir);
    const specPath = join(dir, 'openapi.json');
    try {
      specText = readFileSync(specPath, 'utf8');
    } catch {
      throw new Error(
        `no OpenAPI spec at ${specPath} — run \`palbase spec\` first, or pass --url to fetch it from an origin`,
      );
    }
  }
  // THE ROLES FOLLOW THE CONTRACT, in both modes. They used to come from the
  // linked directory while --url redirected only where the contract was read
  // from — so a remote contract could be generated against a LOCAL checkout's
  // role list, and the two could disagree. Now there is one source and no way
  // for them to drift apart.
  writeGenerated(
    parseOpenAPI(specText),
    cfg,
    outFile,
    out,
    readSpecDeploy(specText),
    parseRooms(specText),
    readRolesFromSpec(specText),
  );
}

// --- Watch mode (tswatch.go port) --------------------------------------------

export type FetchFn = (url: string) => Promise<string>;

// watchLoop polls specURL on each tick:
//   - Fetch fails: print a down message ONCE per transition — a waiting line
//     when nothing is listening, or the HTTP-error variant when the origin
//     answered with an error status.
//   - Fetch succeeds: SHA-256 the body. Same hash as the last EMIT → skip.
//     Same hash as the last WARNED-bad body → stay silent. Otherwise parse +
//     emit + write; a bad body (unparseable, or 0 ops with an existing file to
//     protect) warns once and records its hash. A successful emit resets the
//     bad-hash state.
// Hash state starts EMPTY intentionally: the first successful fetch always
// regenerates. Returns (printing "watch stopped") when the tick source ends.
export async function watchLoop(
  specURL: string,
  cfg: GeneratedConfig,
  outFile: string,
  fetchFn: FetchFn,
  ticks: AsyncIterable<void>,
  out: Writer,
): Promise<void> {
  let lastEmittedHash = ''; // '' = nothing emitted yet
  let lastBadHash = ''; // '' = no standing bad-body warning
  let lastDownMsg = ''; // last printed down message; '' = the origin was up

  for await (const _ of ticks) {
    let specText: string;
    try {
      specText = await fetchFn(specURL);
    } catch (err) {
      let m = `waiting for ${specURL}…`;
      if (err instanceof HttpStatusError) {
        m = `${specURL} responded with an error (HTTP ${err.status})`;
      }
      if (m !== lastDownMsg) {
        out(m);
        lastDownMsg = m;
      }
      continue;
    }

    // The origin is (back) up.
    lastDownMsg = '';

    const h = createHash('sha256').update(specText).digest('hex');
    if (lastEmittedHash !== '' && h === lastEmittedHash) continue; // no change
    if (lastBadHash !== '' && h === lastBadHash) continue; // already warned

    let ops: Op[];
    let rooms: RoomDecl[];
    try {
      ops = parseOpenAPI(specText);
      rooms = parseRooms(specText);
    } catch (err) {
      out(`warning: failed to parse spec: ${errMsg(err)}`);
      lastBadHash = h;
      continue;
    }
    if (ops.length === 0 && existingNonEmpty(outFile)) {
      out(
        `warning: live spec has 0 operations — keeping existing ${outFile} (fix your controllers and rerun)`,
      );
      lastBadHash = h;
      continue;
    }

    try {
      // FROM THIS TICK'S OWN CONTRACT. The loop used to take a `() => string`
      // provider that defaulted to '' — a caller that omitted it generated
      // role-less modules in silence, and the loop re-read a file on every emit
      // to stay current. Both problems go away when the roles are in the
      // document the tick already fetched.
      emitAndWrite(ops, cfg, outFile, rooms, readRolesFromSpec(specText));
    } catch (err) {
      // Environmental write failure (disk) — not spec-dependent, so do not
      // record a bad hash; the next tick retries.
      out(`warning: codegen error: ${errMsg(err)}`);
      continue;
    }

    lastEmittedHash = h;
    lastBadHash = ''; // a good emit resets the warn-dedup state
    out(`regenerated ${outFile} (${ops.length} operations${provenance(readSpecDeploy(specText))})`);
  }
  out('watch stopped');
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const onAbort = () => {
      clearTimeout(t);
      resolve();
    };
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

async function* intervalTicks(ms: number, signal: AbortSignal): AsyncGenerator<void> {
  while (!signal.aborted) {
    await delay(ms, signal);
    if (signal.aborted) return;
    yield;
  }
}

async function runWatch(
  dir: string,
  outFile: string,
  url: string | undefined,
  out: Writer,
): Promise<void> {
  // readWebConfig throws a clear, actionable error when {dir} was never
  // linked — that's the same "run `palbase link`" failure every other
  // dir-mode path already has, so an unlinked --watch fails loudly here, not
  // by silently retrying an address nothing answers.
  const linked = readWebConfig(dir);

  // No --url: watch the linked checkout's OWN Environment. `palbase serve`
  // (retired) used to expose the spec on http://localhost:4003; nothing
  // replaces that local server today, so defaulting to it just polled a dead
  // address forever with a misleading "waiting for..." message. base_url —
  // already read and validated above — is a real, currently-serving origin.
  const origin = (url ?? linked.url).replace(/\/+$/, '');
  const specURL = `${origin}/openapi.json`;

  // The spec comes from the resolved origin, while identity remains bound to
  // the linked Environment. Initial fetch failure is soft because the loop
  // exists to wait for that origin to answer.
  const cfg: GeneratedConfig = { ...linked, url: origin };
  // WRITTEN BEFORE THE FIRST FETCH, and this is not a nicety: watchLoop does
  // NOT go through writeGenerated (it calls emitAndWrite directly) and skips
  // emit entirely on a zero-op tick, so a config wired only into writeGenerated
  // would never be written in a watch session at all. Placing it before the try
  // also means an origin that never answers still leaves a correct config.
  // `cfg` is const and never reassigned for the life of the session, so there
  // is nothing to refresh per tick.
  writeEnvironmentConfig(outFile, cfg);
  try {
    const initial = await fetchSpec(specURL);
    writeGenerated(
      parseOpenAPI(initial),
      cfg,
      outFile,
      out,
      readSpecDeploy(initial),
      parseRooms(initial),
      readRolesFromSpec(initial),
    );
  } catch (err) {
    out(`warning: codegen skipped (${errMsg(err)})`);
  }

  // A real Ctrl-C aborts the tick source → clean "watch stopped" exit 0
  // instead of a hard kill that could truncate palbe.gen.ts mid-write.
  const ctrl = new AbortController();
  const stop = () => ctrl.abort();
  process.once('SIGINT', stop);
  process.once('SIGTERM', stop);
  try {
    await watchLoop(specURL, cfg, outFile, fetchSpec, intervalTicks(1000, ctrl.signal), out);
  } finally {
    process.removeListener('SIGINT', stop);
    process.removeListener('SIGTERM', stop);
  }
}

// --- Entry -------------------------------------------------------------------

const stdoutWriter: Writer = (line) => {
  process.stdout.write(`${line}\n`);
};
const stderrWriter: Writer = (line) => {
  process.stderr.write(`${line}\n`);
};

function parseCliArgs(argv: string[]) {
  return parseArgs({
    args: argv,
    options: {
      dir: { type: 'string', default: 'palbase/environments' },
      out: { type: 'string', default: 'palbe.gen.ts' },
      url: { type: 'string' },
      soft: { type: 'boolean', default: false },
      watch: { type: 'boolean', default: false },
      help: { type: 'boolean', short: 'h', default: false },
    },
  }).values;
}

export async function main(
  argv: string[],
  out: Writer = stdoutWriter,
  errOut: Writer = stderrWriter,
): Promise<number> {
  let values: ReturnType<typeof parseCliArgs>;
  try {
    values = parseCliArgs(argv);
  } catch (e) {
    errOut(`error: ${errMsg(e)}`);
    errOut(USAGE);
    return 1;
  }
  if (values.help) {
    out(USAGE);
    return 0;
  }
  try {
    const environment = resolveEnvironment(process.env.PALBASE_ENV);
    const envDir = environmentDir(values.dir, environment);
    // `--out` is a NAME resolved inside the environment's directory; an explicit
    // path is honoured as given, because a caller that spells one out means it.
    const outFile = isAbsolute(values.out) ? values.out : join(envDir, values.out);
    // THE BARREL GOES FIRST, and in watch mode that is the whole point.
    //
    // `runWatch` does not return until Ctrl-C. Writing the barrel after it meant
    // a fresh checkout running `palbe-gen --watch` produced the client but never
    // the one line the application imports — and worse, switching PALBASE_ENV
    // and re-watching left the barrel naming the OLD environment for the whole
    // session, so the app talked to the wrong stack. That is precisely the
    // defect this layout exists to remove.
    //
    // It is derived from --dir, NOT from the process's working directory: a
    // `palbe-gen --dir apps/web/palbase/environments` run from the repository
    // root used to write a SECOND visible `palbase/` at the root while the app's
    // own barrel was never written at all.
    //
    // AMA ÖNCE DOĞRULA. "Barrel önce gider" watch DÖNGÜSÜNDEN önce demek,
    // KONFİGÜRASYON DOĞRULAMASINDAN önce değil — ve ikisini karıştırmak bu
    // dosyanın kendi testlerini düşürdü: `dir mode` bloğunun on iki vakası da
    // konfigürasyon hatasını bekliyor, yani sıra zaten testlerde yazılı.
    //
    // ÖLÇÜLDÜ: `--dir` yazılabilir bir kökün iki seviye altında değilse
    // `barrelRootFor` erişilemez bir dizine çıkıyor ve mkdir konfigürasyon
    // hiç okunmadan patlıyor — `--dir /tmp/x` verildiğinde kök `/private` olup
    // `error: EACCES: permission denied, mkdir '/private/palbase'` geliyordu.
    // Kusur `TMPDIR` sayesinde GİZLİYDİ (iki seviye yukarısı yazılabilir bir
    // kullanıcı dizini oluyordu); turbo 2 STRICT env kipinde onu sildiği için
    // `npm test` 915/915 yeşilken `turbo run test` aynı dosyada 12 vaka
    // düşürüyordu — ve CI yalnız turbo yolunu koşuyor.
    //
    // Konfigürasyon burada BİR KEZ FAZLADAN okunuyor (asıl okuma
    // generateOnce/runWatch içinde). Bir JSON okumasının bedeli, yazmadan önce
    // doğrulamanın karşılığında ucuz.
    // THE LEAF GOES WITH THE BARRELS, not later with the client.
    //
    // The barrels are written here, unconditionally, before anything can fail.
    // The leaf used to be written only inside writeGenerated/runWatch, so a run
    // that ended early — `--soft` with no spec in the selected environment, the
    // exact shape `predev` produces on a fresh environment — left
    // `palbase/config.ts` re-exporting a file that was never created. Measured:
    // `PALBASE_ENV=staging … --soft` exits 0 and the app then fails to build on
    // an import the generator itself emitted. `client.ts` can dangle the same
    // way, but checkouts COMMIT `<env>/palbe.gen.ts` and do not commit
    // `palbe.config.ts`, so this one dangles alone — on projects that built
    // yesterday.
    //
    // `readWebConfig` is the same call this path already made to validate
    // before writing; `--url` runs let generateOnce/runWatch overwrite the leaf
    // with the resolved origin.
    const linked = readWebConfig(envDir);
    writeEnvironmentConfig(outFile, linked);
    writeClientBarrel(barrelRootFor(values.dir), outFile);
    writeConfigBarrel(barrelRootFor(values.dir), outFile);
    out(
      `environment ${environment} → ${join(barrelRootFor(values.dir), 'palbase', 'client.ts')}` +
        ` + ${join(barrelRootFor(values.dir), 'palbase', 'config.ts')}`,
    );
    if (values.watch) {
      await runWatch(envDir, outFile, values.url, out);
    } else {
      await generateOnce(envDir, outFile, values.url, out);
    }
    return 0;
  } catch (err) {
    // --soft turns ANY failure into a warning + exit 0, so a predev/prebuild
    // hook never blocks a machine without the spec or a running serve.
    if (values.soft) {
      out(`warning: codegen skipped (${errMsg(err)})`);
      return 0;
    }
    errOut(`error: ${errMsg(err)}`);
    return 1;
  }
}
